import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from 'react-oidc-context';
import { getSiteContent, updateSiteContent } from '../../api/siteContentApi';
import { CDN_BASE_URL } from '../../config';
import { media } from '../../styles/theme';

// ==================== Default Data ====================

const DEFAULT_ACHIEVEMENT_EN = {
  title: 'GATE Selective Entry Exam',
  subtitle: 'Outstanding Results from Uplus Education Students',
  items: [
    { text: '4 students scored above 300 in the 2025 GATE exam (302 / 307 / 308 / 309 — all from the same 5-student advanced class).', highlights: ['4 students'] },
    { text: '1 student achieved the State Highest score in 2025 GATE Quantitative Reasoning.', highlights: ['1 student'] },
    { text: '4 students have achieved the State Highest score in 2025 GATE Communicating Ideas.', highlights: ['4 students'] },
    { text: '30+ students receive Perth Modern Offers or private school scholarships every year.', highlights: ['30+ students'] },
  ],
  body: [
    { text: 'Behind every result is a long-term, sustainable teaching system. To date, Uplus Education has helped over 260 students gain entry into GATE programs or receive private school scholarships.', highlights: ['260 students'] },
    { text: 'We have always believed that results are an outcome, not the sole objective. Our achievements stem from systematic curriculum design, consistent teaching execution, strong parental support, and the daily effort and good study habits cultivated by our students.' },
    { text: 'Every outstanding result is the natural product of collective commitment and long-term dedication.' },
  ],
};

const DEFAULT_ACHIEVEMENT_ZH = {
  title: 'GATE Selective Entry Exam',
  subtitle: 'Uplus Education 学员荣获佳绩',
  items: [
    { text: '4名学员在2025年GATE考试中总分突破300分（302 / 307 / 308 / 309 —— 来自同一个5人封闭高阶班级）。', highlights: ['4名'] },
    { text: '1名学员在2025年GATE考试中荣获Quantitative Reasoning State Highest 数学逻辑州最高分。', highlights: ['1名'] },
    { text: '4名学员在2025年GATE考试中荣获Communicating Ideas State Highest 写作州最高分。', highlights: ['4名'] },
    { text: '每年30+学生都获得了Perth Modern Offer / 私校奖学金。', highlights: ['每年30+'] },
  ],
  body: [
    { text: '成绩的背后，是长期、可持续的教学体系。在过往教学中，Uplus Education 累计已有260+名学员成功考入GATE班级或获得私校奖学金。', highlights: ['260+名'] },
    { text: '我们始终相信，成绩只是结果，而非唯一目标。Uplus的教学成果，来自系统化的课程设计、长期坚持的教学执行、家长的高度配合，以及学生日复一日的努力与良好学习习惯的养成。' },
    { text: '每一份亮眼成绩，都是多方共同投入、长期积累后的自然结果。' },
  ],
};

// Render text with highlighted segments (first match of each highlight string)
const renderWithHighlights = (text, highlights) => {
  if (!highlights || highlights.length === 0 || !text) return text;

  const matches = [];
  for (const h of highlights) {
    if (!h) continue;
    const idx = text.indexOf(h);
    if (idx !== -1) {
      matches.push({ start: idx, end: idx + h.length, text: h });
    }
  }
  if (matches.length === 0) return text;

  matches.sort((a, b) => a.start - b.start);
  const filtered = [matches[0]];
  for (let i = 1; i < matches.length; i++) {
    if (matches[i].start >= filtered[filtered.length - 1].end) {
      filtered.push(matches[i]);
    }
  }

  const segments = [];
  let pos = 0;
  filtered.forEach((m, i) => {
    if (pos < m.start) segments.push(text.slice(pos, m.start));
    segments.push(<span key={i} style={{ color: '#ef4444', fontWeight: 700 }}>{m.text}</span>);
    pos = m.end;
  });
  if (pos < text.length) segments.push(text.slice(pos));

  return segments;
};

const DEFAULT_COURSES = [
  {
    id: 1,
    title: 'Pre-Primary - Year 3 Extension',
    description: 'Ignite a lifelong love for learning with our engaging Pre-Primary to Year 3 Extension program. Tailored for young minds, this course builds strong foundations in literacy and numeracy through fun, interactive lessons. Our expert tutors foster critical thinking and confidence, ensuring your child thrives in their early educational journey and beyond.',
    image: 'english.webp'
  },
  {
    id: 2,
    title: 'Year 4 - Year 6 (Term 1) Gate',
    description: 'Unlock your child\'s potential with our Year 4-5 GATE preparation program. The Gifted and Talented Education (GATE) program selects students for specialized academic opportunities in Western Australia\'s top schools. Our targeted curriculum sharpens analytical thinking and problem-solving skills through challenging, dynamic lessons, empowering students to excel in GATE entrance exams and secure their place in gifted programs.',
    image: 'gate.png'
  },
  {
    id: 3,
    title: 'Year 6 (Term 2) - Year 10 Extension',
    description: 'Elevate academic performance with our Year 6-10 Extension program. Designed for ambitious students, this course offers enriched content and accelerated learning in core subjects. Our expert tutors guide students to master advanced concepts, hone critical analysis, and develop effective study habits, paving the way for outstanding success in senior secondary school.',
    image: 'y6english_writing.jpg'
  },
  {
    id: 4,
    title: 'Year 10 - Year 12 ATAR Program',
    description: 'Achieve university dreams with our intensive Year 10-12 ATAR program. The Australian Tertiary Admission Rank (ATAR) determines university entry in Australia, and our expert tutors provide comprehensive support across all ATAR subjects. With personalized coaching, proven exam strategies, and time management skills, students are equipped to maximize their scores and secure their future.',
    image: 'atar.jpg'
  },
  {
    id: 5,
    title: 'Other Specialised Courses',
    description: 'We also offer specialised courses designed to target key academic skills. These include:',
    specializedList: [
      'Writing Courses: to build strong structure, vocabulary, and expression across various text types.',
      'Reading Comprehension: to develop critical thinking, inference, and analysis skills.',
      'Novel Study: to enhance literary understanding through guided reading and discussion.',
      'AMC Preparation: to sharpen mathematical reasoning and problem-solving for competition success.'
    ],
    image: 'personalised.png'
  }
];

const DEFAULT_LEADERS = [
  {
    id: 'lucy',
    name: 'Lucy Lu',
    initials: 'LL',
    title: 'Director of Uplus Education',
    image: 'lucy.jpg',
    campus: 'Victoria Park',
    achievements: [
      'Started education profession since 2008',
      'TRBWA registered qualified teacher for Secondary Humanities & Social Science and Mathematics',
      'Graduated with a master\'s degree in Secondary Education from Curtin',
      'Graduated with a bachelor\'s degree in Commerce from UWA',
      'Had teaching experience in multiple leading Western Australian high schools',
      'Awarded Outstanding Australian Education and Teaching Contribution Award by ACWF in 2021',
      'Awarded scholarships by Department of Education WA for studies in Year 11 and Year 12 in 2006'
    ]
  },
  {
    id: 'michael',
    name: 'Michael Kuai',
    initials: 'MK',
    title: 'CAMPUS MANAGER',
    image: 'michael.jpg',
    campus: 'Claremont',
    achievements: [
      'Manager of Uplus Education Claremont Campus',
      'IT Education Manager overseeing tech-integrated curriculum and digital systems',
      'Started STEM education profession since 2020, graduated with a master\'s degree in Cyber Security',
      'Partners with Perth schools and child care centers for STEM programs',
      'Former course developer for DJI\'s education department',
      'Awarded 4th place in Asia-Pacific Lego robotics competition, 2016'
    ]
  },
  {
    id: 'simon',
    name: 'Simon Liu',
    initials: 'SL',
    title: 'EDUCATOR',
    image: 'simon.jpg',
    campus: 'Victoria Park',
    achievements: [
      'A highly qualified educator with a strong academic background and a passion for teaching',
      'Completed both undergraduate and postgraduate studies in Physiotherapy at Curtin University, where entry into the program required an ATAR above 98',
      'Named to the Curtin University Vice Chancellor\'s List in 2002, an honour reserved for students achieving results in the top 1% across the university'
    ]
  }
];

// ==================== Styled Components ====================

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  ${media.phone} {
    padding: 16px 12px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 24px;
  gap: 4px;

  /* 手机上 tab 横向滚动，避免挤压/溢出 */
  ${media.phone} {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background: ${props => props.$active ? '#F1AF59' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#F1AF59' : '#f3f4f6'};
  }

  ${media.phone} {
    padding: 11px 16px;
    white-space: nowrap;
    min-height: 44px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$variant === 'danger' ? '#ef4444' : props.$variant === 'secondary' ? '#6b7280' : '#F1AF59'};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  background: ${props => props.$type === 'error' ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.$type === 'error' ? '#dc2626' : '#16a34a'};
  border: 1px solid ${props => props.$type === 'error' ? '#fecaca' : '#bbf7d0'};
`;

const ItemCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fafafa;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ItemIndex = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const RemoveButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #ef4444;
  border-radius: 6px;
  background: white;
  color: #ef4444;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fef2f2;
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  width: 100%;

  &:hover {
    border-color: #F1AF59;
    color: #F1AF59;
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewPanel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  background: white;
  max-height: 80vh;
  overflow-y: auto;
`;

const PreviewTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

// Achievement preview styles
const PreviewAchievementCard = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  padding: 32px;
  color: white;
`;

const PreviewAchievementTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 4px;
`;

const PreviewAchievementSubtitle = styled.p`
  font-size: 14px;
  text-align: center;
  opacity: 0.8;
  margin-bottom: 16px;
`;

const PreviewDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255,255,255,0.2);
  margin: 16px 0;
`;

const PreviewItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
`;

const PreviewBody = styled.p`
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.9;
  margin: 8px 0;
`;

// Course preview styles
const PreviewCourseRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const PreviewCourseImage = styled.div`
  width: 200px;
  min-width: 200px;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PreviewCourseContent = styled.div`
  flex: 1;
`;

const PreviewCourseTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const PreviewCourseDesc = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;
`;

// Team preview styles
const PreviewLeaderCard = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
`;

const PreviewLeaderAvatar = styled.div`
  width: 100px;
  min-width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  background: #F1AF59;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 24px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PreviewLeaderInfo = styled.div`
  flex: 1;
`;

const PreviewLeaderName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 2px 0;
`;

const PreviewLeaderTitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 8px 0;
`;

const PreviewAchievementList = styled.ul`
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
`;

const MoveButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #6b7280;
  font-size: 16px;
`;

const SpecializedListInput = styled.div`
  margin-top: 8px;
`;

const SpecializedItem = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  align-items: center;
`;

const SpecializedInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }
`;

const SmallRemoveBtn = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background: #fee2e2;
  color: #ef4444;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fecaca;
  }
`;

const SmallAddBtn = styled.button`
  padding: 4px 12px;
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  margin-top: 4px;

  &:hover {
    border-color: #F1AF59;
    color: #F1AF59;
  }
`;

// ==================== Component ====================

const SiteSettings = () => {
  const auth = useAuth();
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const canEdit = userGroups.includes('portal_director') ||
                  userGroups.includes('portal_system_admin') ||
                  userGroups.includes('portal_developer');

  const [activeTab, setActiveTab] = useState('achievement');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Data states
  const [achievementEn, setAchievementEn] = useState(DEFAULT_ACHIEVEMENT_EN);
  const [achievementZh, setAchievementZh] = useState(DEFAULT_ACHIEVEMENT_ZH);
  const [courses, setCourses] = useState(DEFAULT_COURSES);
  const [leaders, setLeaders] = useState(DEFAULT_LEADERS);

  // Track which sections have been customized
  const [customized, setCustomized] = useState({});

  // Load all content on mount
  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      try {
        const [enData, zhData, coursesData, leadersData] = await Promise.all([
          getSiteContent('home_achievement_en'),
          getSiteContent('home_achievement_zh'),
          getSiteContent('courses'),
          getSiteContent('team_leaders'),
        ]);

        const customizedMap = {};

        if (enData) {
          setAchievementEn(enData);
          customizedMap.home_achievement_en = true;
        }
        if (zhData) {
          setAchievementZh(zhData);
          customizedMap.home_achievement_zh = true;
        }
        if (coursesData) {
          setCourses(coursesData);
          customizedMap.courses = true;
        }
        if (leadersData) {
          setLeaders(leadersData);
          customizedMap.team_leaders = true;
        }

        setCustomized(customizedMap);
      } catch (error) {
        console.error('Error loading site content:', error);
      }
      setLoading(false);
    };

    loadContent();
  }, []);

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  }, []);

  const handleSave = async (sectionId, content) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await updateSiteContent(sectionId, content);
      setCustomized(prev => ({ ...prev, [sectionId]: true }));
      showMessage(`Saved successfully!`);
    } catch (error) {
      showMessage(error.message, 'error');
    }
    setSaving(false);
  };

  const handleReset = async (sectionId) => {
    if (!canEdit) return;
    if (!window.confirm('Reset to default content? This will remove your customizations.')) return;

    setSaving(true);
    try {
      let defaultContent;
      switch (sectionId) {
        case 'home_achievement_en':
          defaultContent = DEFAULT_ACHIEVEMENT_EN;
          setAchievementEn(defaultContent);
          break;
        case 'home_achievement_zh':
          defaultContent = DEFAULT_ACHIEVEMENT_ZH;
          setAchievementZh(defaultContent);
          break;
        case 'courses':
          defaultContent = DEFAULT_COURSES;
          setCourses(defaultContent);
          break;
        case 'team_leaders':
          defaultContent = DEFAULT_LEADERS;
          setLeaders(defaultContent);
          break;
        default:
          break;
      }
      await updateSiteContent(sectionId, defaultContent);
      setCustomized(prev => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      showMessage('Reset to default successfully!');
    } catch (error) {
      showMessage(error.message, 'error');
    }
    setSaving(false);
  };

  if (!canEdit) {
    return (
      <Container>
        <Alert $type="error">You do not have permission to access Site Settings. Only Director and System Admin can manage site content.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingOverlay>Loading site content...</LoadingOverlay>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Site Settings</Title>
        <Subtitle>Manage editable content on public pages. Changes are reflected immediately.</Subtitle>
      </Header>

      {message && <Alert $type={message.type}>{message.text}</Alert>}

      <TabBar>
        <Tab $active={activeTab === 'achievement'} onClick={() => setActiveTab('achievement')}>
          Home - Achievements
        </Tab>
        <Tab $active={activeTab === 'courses'} onClick={() => setActiveTab('courses')}>
          Courses
        </Tab>
        <Tab $active={activeTab === 'team'} onClick={() => setActiveTab('team')}>
          Team Leaders
        </Tab>
      </TabBar>

      {activeTab === 'achievement' && (
        <AchievementEditor
          achievementEn={achievementEn}
          achievementZh={achievementZh}
          setAchievementEn={setAchievementEn}
          setAchievementZh={setAchievementZh}
          onSave={handleSave}
          onReset={handleReset}
          saving={saving}
          customized={customized}
        />
      )}

      {activeTab === 'courses' && (
        <CoursesEditor
          courses={courses}
          setCourses={setCourses}
          onSave={handleSave}
          onReset={handleReset}
          saving={saving}
          customized={customized}
        />
      )}

      {activeTab === 'team' && (
        <TeamEditor
          leaders={leaders}
          setLeaders={setLeaders}
          onSave={handleSave}
          onReset={handleReset}
          saving={saving}
          customized={customized}
        />
      )}
    </Container>
  );
};

// ==================== Achievement Editor ====================

const AchievementEditor = ({ achievementEn, achievementZh, setAchievementEn, setAchievementZh, onSave, onReset, saving, customized }) => {
  const [lang, setLang] = useState('en');
  const data = lang === 'en' ? achievementEn : achievementZh;
  const setData = lang === 'en' ? setAchievementEn : setAchievementZh;
  const sectionId = lang === 'en' ? 'home_achievement_en' : 'home_achievement_zh';

  const updateField = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const updateItemText = (index, value) => {
    setData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, text: value } : item)
    }));
  };

  const updateItemHighlight = (index, hIdx, value) => {
    setData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const newH = [...(item.highlights || [])];
        newH[hIdx] = value;
        return { ...item, highlights: newH };
      })
    }));
  };

  const addItemHighlight = (index) => {
    setData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, highlights: [...(item.highlights || []), ''] } : item)
    }));
  };

  const removeItemHighlight = (index, hIdx) => {
    setData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        return { ...item, highlights: (item.highlights || []).filter((_, j) => j !== hIdx) };
      })
    }));
  };

  const addItem = () => {
    setData(prev => ({
      ...prev,
      items: [...prev.items, { text: '', highlights: [] }]
    }));
  };

  const removeItem = (index) => {
    setData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateBodyText = (index, value) => {
    setData(prev => ({
      ...prev,
      body: prev.body.map((para, i) => i === index ? { ...para, text: value } : para)
    }));
  };

  const updateBodyHighlight = (index, hIdx, value) => {
    setData(prev => ({
      ...prev,
      body: prev.body.map((para, i) => {
        if (i !== index) return para;
        const newH = [...(para.highlights || [])];
        newH[hIdx] = value;
        return { ...para, highlights: newH };
      })
    }));
  };

  const addBodyHighlight = (index) => {
    setData(prev => ({
      ...prev,
      body: prev.body.map((para, i) => i === index ? { ...para, highlights: [...(para.highlights || []), ''] } : para)
    }));
  };

  const removeBodyHighlight = (index, hIdx) => {
    setData(prev => ({
      ...prev,
      body: prev.body.map((para, i) => {
        if (i !== index) return para;
        return { ...para, highlights: (para.highlights || []).filter((_, j) => j !== hIdx) };
      })
    }));
  };

  const addBody = () => {
    setData(prev => ({
      ...prev,
      body: [...prev.body, { text: '', highlights: [] }]
    }));
  };

  const removeBody = (index) => {
    setData(prev => ({
      ...prev,
      body: prev.body.filter((_, i) => i !== index)
    }));
  };

  return (
    <TwoColumn>
      <div>
        <Section>
          <SectionTitle>
            Achievement Section ({lang === 'en' ? 'English' : 'Chinese'})
            {customized[sectionId] && <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 8 }}>(Customized)</span>}
          </SectionTitle>

          <ButtonRow style={{ marginTop: 0, marginBottom: 16 }}>
            <ActionButton $variant={lang === 'en' ? undefined : 'secondary'} onClick={() => setLang('en')} style={{ padding: '6px 16px', fontSize: 13 }}>
              English
            </ActionButton>
            <ActionButton $variant={lang === 'zh' ? undefined : 'secondary'} onClick={() => setLang('zh')} style={{ padding: '6px 16px', fontSize: 13 }}>
              Chinese
            </ActionButton>
          </ButtonRow>

          <FormGroup>
            <Label>Title</Label>
            <Input value={data.title} onChange={e => updateField('title', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Subtitle</Label>
            <Input value={data.subtitle} onChange={e => updateField('subtitle', e.target.value)} />
          </FormGroup>

          <SectionTitle style={{ marginTop: 24 }}>Achievement Items</SectionTitle>
          {data.items.map((item, i) => (
            <ItemCard key={i}>
              <ItemHeader>
                <ItemIndex>Item {i + 1}</ItemIndex>
                <RemoveButton onClick={() => removeItem(i)}>Remove</RemoveButton>
              </ItemHeader>
              <FormGroup>
                <Label>Text</Label>
                <TextArea value={item.text} onChange={e => updateItemText(i, e.target.value)} style={{ minHeight: 50 }} />
              </FormGroup>
              <FormGroup>
                <Label>Highlights (paste text to make red — first match will be highlighted)</Label>
                {(item.highlights || []).map((h, j) => (
                  <SpecializedItem key={j}>
                    <SpecializedInput value={h} onChange={e => updateItemHighlight(i, j, e.target.value)} placeholder="Paste text from above to highlight" />
                    <SmallRemoveBtn onClick={() => removeItemHighlight(i, j)}>X</SmallRemoveBtn>
                  </SpecializedItem>
                ))}
                <SmallAddBtn onClick={() => addItemHighlight(i)}>+ Add highlight</SmallAddBtn>
              </FormGroup>
            </ItemCard>
          ))}
          <AddButton onClick={addItem}>+ Add Achievement Item</AddButton>

          <SectionTitle style={{ marginTop: 24 }}>Body Paragraphs</SectionTitle>
          {data.body.map((para, i) => (
            <ItemCard key={i}>
              <ItemHeader>
                <ItemIndex>Paragraph {i + 1}</ItemIndex>
                <RemoveButton onClick={() => removeBody(i)}>Remove</RemoveButton>
              </ItemHeader>
              <FormGroup>
                <Label>Text</Label>
                <TextArea value={para.text} onChange={e => updateBodyText(i, e.target.value)} />
              </FormGroup>
              <FormGroup>
                <Label>Highlights (paste text to make red, optional)</Label>
                {(para.highlights || []).map((h, j) => (
                  <SpecializedItem key={j}>
                    <SpecializedInput value={h} onChange={e => updateBodyHighlight(i, j, e.target.value)} placeholder="Paste text from above to highlight" />
                    <SmallRemoveBtn onClick={() => removeBodyHighlight(i, j)}>X</SmallRemoveBtn>
                  </SpecializedItem>
                ))}
                <SmallAddBtn onClick={() => addBodyHighlight(i)}>+ Add highlight</SmallAddBtn>
              </FormGroup>
            </ItemCard>
          ))}
          <AddButton onClick={addBody}>+ Add Body Paragraph</AddButton>

          <ButtonRow>
            <ActionButton onClick={() => onSave(sectionId, data)} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </ActionButton>
            <ActionButton $variant="secondary" onClick={() => onReset(sectionId)} disabled={saving}>
              Reset to Default
            </ActionButton>
          </ButtonRow>
        </Section>
      </div>

      <div>
        <PreviewPanel>
          <PreviewTitle>Preview</PreviewTitle>
          <PreviewAchievementCard>
            <PreviewAchievementTitle>{data.title}</PreviewAchievementTitle>
            <PreviewAchievementSubtitle>{data.subtitle}</PreviewAchievementSubtitle>
            <PreviewDivider />
            {data.items.map((item, i) => (
              <PreviewItem key={i}>
                <span style={{ marginTop: 2 }}>&#8226;</span>
                <span>{renderWithHighlights(item.text, item.highlights)}</span>
              </PreviewItem>
            ))}
            <PreviewDivider />
            {data.body.map((para, i) => (
              <PreviewBody key={i}>
                {renderWithHighlights(para.text, para.highlights)}
              </PreviewBody>
            ))}
          </PreviewAchievementCard>
        </PreviewPanel>
      </div>
    </TwoColumn>
  );
};

// ==================== Courses Editor ====================

const CoursesEditor = ({ courses, setCourses, onSave, onReset, saving, customized }) => {
  const updateCourse = (index, field, value) => {
    setCourses(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addCourse = () => {
    setCourses(prev => [
      ...prev,
      {
        id: Date.now(),
        title: 'New Course',
        description: '',
        image: '',
        specializedList: null
      }
    ]);
  };

  const removeCourse = (index) => {
    if (!window.confirm('Remove this course?')) return;
    setCourses(prev => prev.filter((_, i) => i !== index));
  };

  const moveCourse = (index, direction) => {
    setCourses(prev => {
      const newList = [...prev];
      const target = index + direction;
      if (target < 0 || target >= newList.length) return prev;
      [newList[index], newList[target]] = [newList[target], newList[index]];
      return newList;
    });
  };

  const updateSpecializedItem = (courseIndex, itemIndex, value) => {
    setCourses(prev => prev.map((c, i) => {
      if (i !== courseIndex) return c;
      const newList = [...(c.specializedList || [])];
      newList[itemIndex] = value;
      return { ...c, specializedList: newList };
    }));
  };

  const addSpecializedItem = (courseIndex) => {
    setCourses(prev => prev.map((c, i) => {
      if (i !== courseIndex) return c;
      return { ...c, specializedList: [...(c.specializedList || []), ''] };
    }));
  };

  const removeSpecializedItem = (courseIndex, itemIndex) => {
    setCourses(prev => prev.map((c, i) => {
      if (i !== courseIndex) return c;
      const newList = (c.specializedList || []).filter((_, j) => j !== itemIndex);
      return { ...c, specializedList: newList.length > 0 ? newList : null };
    }));
  };

  const toggleSpecializedList = (courseIndex) => {
    setCourses(prev => prev.map((c, i) => {
      if (i !== courseIndex) return c;
      if (c.specializedList) {
        return { ...c, specializedList: null };
      }
      return { ...c, specializedList: [''] };
    }));
  };

  return (
    <TwoColumn>
      <div>
        <Section>
          <SectionTitle>
            Courses
            {customized.courses && <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 8 }}>(Customized)</span>}
          </SectionTitle>

          {courses.map((course, i) => (
            <ItemCard key={course.id || i}>
              <ItemHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ItemIndex>Course {i + 1}</ItemIndex>
                  <MoveButton onClick={() => moveCourse(i, -1)} disabled={i === 0} title="Move up">&#8593;</MoveButton>
                  <MoveButton onClick={() => moveCourse(i, 1)} disabled={i === courses.length - 1} title="Move down">&#8595;</MoveButton>
                </div>
                <RemoveButton onClick={() => removeCourse(i)}>Remove</RemoveButton>
              </ItemHeader>
              <FormGroup>
                <Label>Title</Label>
                <Input value={course.title} onChange={e => updateCourse(i, 'title', e.target.value)} />
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <TextArea value={course.description} onChange={e => updateCourse(i, 'description', e.target.value)} />
              </FormGroup>
              <FormGroup>
                <Label>Image filename (in CDN /static/course/)</Label>
                <Input value={course.image} onChange={e => updateCourse(i, 'image', e.target.value)} placeholder="e.g. gate.png" />
                {course.image && (
                  <img
                    src={`${CDN_BASE_URL}/static/course/${course.image}`}
                    alt="preview"
                    style={{ marginTop: 8, maxHeight: 80, borderRadius: 4, display: 'block' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </FormGroup>
              <FormGroup>
                <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Specialized List
                  <SmallAddBtn onClick={() => toggleSpecializedList(i)}>
                    {course.specializedList ? 'Remove list' : '+ Add list'}
                  </SmallAddBtn>
                </Label>
                {course.specializedList && (
                  <SpecializedListInput>
                    {course.specializedList.map((item, j) => (
                      <SpecializedItem key={j}>
                        <SpecializedInput value={item} onChange={e => updateSpecializedItem(i, j, e.target.value)} placeholder="Specialized course description" />
                        <SmallRemoveBtn onClick={() => removeSpecializedItem(i, j)}>X</SmallRemoveBtn>
                      </SpecializedItem>
                    ))}
                    <SmallAddBtn onClick={() => addSpecializedItem(i)}>+ Add item</SmallAddBtn>
                  </SpecializedListInput>
                )}
              </FormGroup>
            </ItemCard>
          ))}
          <AddButton onClick={addCourse}>+ Add Course</AddButton>

          <ButtonRow>
            <ActionButton onClick={() => onSave('courses', courses)} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </ActionButton>
            <ActionButton $variant="secondary" onClick={() => onReset('courses')} disabled={saving}>
              Reset to Default
            </ActionButton>
          </ButtonRow>
        </Section>
      </div>

      <div>
        <PreviewPanel>
          <PreviewTitle>Preview</PreviewTitle>
          {courses.map((course, i) => (
            <PreviewCourseRow key={course.id || i}>
              <PreviewCourseImage>
                {course.image && (
                  <img
                    src={`${CDN_BASE_URL}/static/course/${course.image}`}
                    alt={course.title}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </PreviewCourseImage>
              <PreviewCourseContent>
                <PreviewCourseTitle>{course.title}</PreviewCourseTitle>
                <PreviewCourseDesc>{course.description}</PreviewCourseDesc>
                {course.specializedList && (
                  <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 13, color: '#4b5563' }}>
                    {course.specializedList.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                )}
              </PreviewCourseContent>
            </PreviewCourseRow>
          ))}
        </PreviewPanel>
      </div>
    </TwoColumn>
  );
};

// ==================== Team Leaders Editor ====================

const TeamEditor = ({ leaders, setLeaders, onSave, onReset, saving, customized }) => {
  const updateLeader = (index, field, value) => {
    setLeaders(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  const updateAchievement = (leaderIndex, achIndex, value) => {
    setLeaders(prev => prev.map((l, i) => {
      if (i !== leaderIndex) return l;
      const newAch = [...l.achievements];
      newAch[achIndex] = value;
      return { ...l, achievements: newAch };
    }));
  };

  const addAchievement = (leaderIndex) => {
    setLeaders(prev => prev.map((l, i) => {
      if (i !== leaderIndex) return l;
      return { ...l, achievements: [...l.achievements, ''] };
    }));
  };

  const removeAchievement = (leaderIndex, achIndex) => {
    setLeaders(prev => prev.map((l, i) => {
      if (i !== leaderIndex) return l;
      return { ...l, achievements: l.achievements.filter((_, j) => j !== achIndex) };
    }));
  };

  const addLeader = () => {
    const id = `leader_${Date.now()}`;
    setLeaders(prev => [
      ...prev,
      {
        id,
        name: 'New Leader',
        initials: 'NL',
        title: '',
        image: '',
        campus: '',
        achievements: ['']
      }
    ]);
  };

  const removeLeader = (index) => {
    if (!window.confirm('Remove this leader card?')) return;
    setLeaders(prev => prev.filter((_, i) => i !== index));
  };

  const moveLeader = (index, direction) => {
    setLeaders(prev => {
      const newList = [...prev];
      const target = index + direction;
      if (target < 0 || target >= newList.length) return prev;
      [newList[index], newList[target]] = [newList[target], newList[index]];
      return newList;
    });
  };

  return (
    <TwoColumn>
      <div>
        <Section>
          <SectionTitle>
            Team Leaders
            {customized.team_leaders && <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 8 }}>(Customized)</span>}
          </SectionTitle>

          {leaders.map((leader, i) => (
            <ItemCard key={leader.id || i}>
              <ItemHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ItemIndex>{leader.name || `Leader ${i + 1}`}</ItemIndex>
                  <MoveButton onClick={() => moveLeader(i, -1)} disabled={i === 0} title="Move up">&#8593;</MoveButton>
                  <MoveButton onClick={() => moveLeader(i, 1)} disabled={i === leaders.length - 1} title="Move down">&#8595;</MoveButton>
                </div>
                <RemoveButton onClick={() => removeLeader(i)}>Remove</RemoveButton>
              </ItemHeader>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormGroup>
                  <Label>Name</Label>
                  <Input value={leader.name} onChange={e => updateLeader(i, 'name', e.target.value)} />
                </FormGroup>
                <FormGroup>
                  <Label>Initials</Label>
                  <Input value={leader.initials} onChange={e => updateLeader(i, 'initials', e.target.value)} placeholder="e.g. LL" />
                </FormGroup>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormGroup>
                  <Label>Title</Label>
                  <Input value={leader.title} onChange={e => updateLeader(i, 'title', e.target.value)} />
                </FormGroup>
                <FormGroup>
                  <Label>Campus</Label>
                  <Input value={leader.campus} onChange={e => updateLeader(i, 'campus', e.target.value)} />
                </FormGroup>
              </div>
              <FormGroup>
                <Label>Image filename (in CDN /static/tutor/leadership/)</Label>
                <Input value={leader.image} onChange={e => updateLeader(i, 'image', e.target.value)} placeholder="e.g. lucy.jpg" />
                {leader.image && (
                  <img
                    src={`${CDN_BASE_URL}/static/tutor/leadership/${leader.image}`}
                    alt="preview"
                    style={{ marginTop: 8, maxHeight: 60, borderRadius: 4, display: 'block' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </FormGroup>
              <FormGroup>
                <Label>Achievements / Qualifications</Label>
                {leader.achievements.map((ach, j) => (
                  <SpecializedItem key={j}>
                    <SpecializedInput value={ach} onChange={e => updateAchievement(i, j, e.target.value)} />
                    <SmallRemoveBtn onClick={() => removeAchievement(i, j)}>X</SmallRemoveBtn>
                  </SpecializedItem>
                ))}
                <SmallAddBtn onClick={() => addAchievement(i)}>+ Add achievement</SmallAddBtn>
              </FormGroup>
            </ItemCard>
          ))}
          <AddButton onClick={addLeader}>+ Add Leader Card</AddButton>

          <ButtonRow>
            <ActionButton onClick={() => onSave('team_leaders', leaders)} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </ActionButton>
            <ActionButton $variant="secondary" onClick={() => onReset('team_leaders')} disabled={saving}>
              Reset to Default
            </ActionButton>
          </ButtonRow>
        </Section>
      </div>

      <div>
        <PreviewPanel>
          <PreviewTitle>Preview</PreviewTitle>
          {leaders.map((leader, i) => (
            <PreviewLeaderCard key={leader.id || i}>
              <PreviewLeaderAvatar>
                {leader.image ? (
                  <img
                    src={`${CDN_BASE_URL}/static/tutor/leadership/${leader.image}`}
                    alt={leader.name}
                    onError={e => { e.target.style.display = 'none'; e.target.parentElement.textContent = leader.initials; }}
                  />
                ) : (
                  leader.initials
                )}
              </PreviewLeaderAvatar>
              <PreviewLeaderInfo>
                <PreviewLeaderName>{leader.name}</PreviewLeaderName>
                <PreviewLeaderTitle>{leader.title}{leader.campus ? ` - ${leader.campus}` : ''}</PreviewLeaderTitle>
                <PreviewAchievementList>
                  {leader.achievements.filter(a => a).map((ach, j) => (
                    <li key={j}>{ach}</li>
                  ))}
                </PreviewAchievementList>
              </PreviewLeaderInfo>
            </PreviewLeaderCard>
          ))}
        </PreviewPanel>
      </div>
    </TwoColumn>
  );
};

export default SiteSettings;
